import { useState, useEffect, useMemo, useCallback } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";
import { decodeEntities } from "../utils/htmlEntities";
import { bumpBufferMinutes } from "../utils/eventDates";
import { withFlatApproval } from "../utils/approvalWrite";
import { pickTransport } from "../utils/transport";
import type { RecurrenceRule } from "../utils/eventRecurrence";

// S2 endpoint — event admin routes
export const EVENT_END_POINT = "event/admin/";

/**
 * Colour map for Mobiscroll calendar events, keyed on the lower-cased `Category`
 * the API computes — NOT on the source id sent in `categories`. `mypymble` is
 * kept as the legacy spelling of `manual`: it is also what an Edval event is
 * still labelled, so should one ever be fetched deliberately it draws in navy
 * rather than falling through to an undefined colour.
 */
const EVENT_COLORS: Record<string, string> = {
	edumate: "#26afd1",
	clipboard: "#26d153",
	/* `manual` is what the API now labels an event created here; `mypymble`
	   survives only on LISS timetable events. */
	manual: "#002f67",
	mypymble: "#7f92e8",
};

/**
 * Build the query string for the list endpoint.
 *
 * `categories` is what makes the calendar readable: unsent, the API returns
 * every source and a normal week is ~1,717 events.
 *
 * An empty selection never reaches here — the caller skips the request entirely
 * (see `fetchEventList`). Sending an empty value would ask for the API default
 * and fill the calendar, which is the opposite of unticking every box.
 *
 * `tags` is the OPPOSITE KIND of parameter and is omitted when empty. Categories
 * are an allow-list; tags narrow a result the API would otherwise return whole,
 * so no tags means no constraint. Sending `&tags=` would be read as no filter
 * anyway, but leaving it off keeps the two meanings from being confused here and
 * keeps the URL — and therefore the cache key — stable for the common case.
 *
 * `supervisors` is opt-in for the same reason it is opt-in on the API: it is
 * two extra queries for the page, and most callers of this function have no use
 * for the answer. The calendar asks for it because its hover card names the
 * owner; the clash panel and the browse views do not, and a `limit=10000`
 * unfiltered sweep should not pay for a roll nobody reads.
 */
function listQueryString(
	from: Date,
	to: Date,
	categories: string[],
	tags: string[] = [],
	supervisors = false,
): string {
	const filter =
		'&filter={"from":"' +
		from.toISOString().split("T")[0] +
		'","to":"' +
		to.toISOString().split("T")[0] +
		'"}';
	return (
		"?limit=10000" +
		filter +
		"&categories=" +
		encodeURIComponent(categories.join(",")) +
		"&include_cancelled=false" +
		(tags.length ? "&tags=" + encodeURIComponent(tags.join(",")) : "") +
		(supervisors ? "&supervisors=1" : "")
	);
}

/**
 * Transform a raw MongoDB Event document into the shape expected by
 * the Mobiscroll calendar and the Home page filter logic.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toCalendarEvent(doc: any): any {
	/* No stored Category means an event created here, which the API labels
	   Manual — the same fallthrough `_categorize_events` uses. */
	const category = (doc.Category || "manual").toLowerCase();
	return {
		...doc,
		id: doc._id,
		/* DISPLAY fields, decoded; the document's own `Title` and `Location` are
		   left exactly as they arrived.

		   That split is the whole point. Edumate sends `Rapha&#235;lle` and React
		   escapes what it renders, so the entity reaches the screen intact — but
		   this app PUTs the document back whole when an event is dragged or bulk
		   tagged, so decoding `Title` in place would quietly rewrite the stored
		   value on the next unrelated save. The views read `title`; the writes
		   read `Title`; neither has to know about the other. */
		title: decodeEntities(doc.Title || ""),
		start: doc.StartDateTime,
		end: doc.EndDateTime,
		event_type: category,
		Source: category,
		location: decodeEntities(
			doc.Location?.Name || doc.Location?.Address || "",
		),
		color: EVENT_COLORS[category] || EVENT_COLORS.manual,
		Attendees: doc.Attendees || [],
		/* Bump in and bump out, as the bands Mobiscroll draws around a schedule
		   event — `bufferBefore` and `bufferAfter`, both in minutes. Derived
		   here rather than in the page because this is where the document
		   becomes a calendar event, and the List, Cards and Home views that
		   also read this shape simply ignore two keys they do not use.

		   THE BUMP OUT HANGS OFF THE END, falling back to the start for an
		   event that has no end — which is how Mobiscroll itself treats one,
		   as a point in the day rather than a span.

		   The stored value is a bare `HH:MM`, so the edge is also what says
		   which DAY to read it against; `bumpBufferMinutes` settles that.

		   Nothing shows on the MONTH grid: those are labels, and a label has no
		   edge to hang a band off. Week and Day are schedules and do draw them.
		   Nor does a date-only event, which sits in the all-day row where there
		   is no hour axis to measure minutes against. */
		bufferBefore: bumpBufferMinutes(doc.StartDateTime, doc.BumpIn, "before"),
		bufferAfter: bumpBufferMinutes(
			doc.EndDateTime || doc.StartDateTime,
			doc.BumpOut,
			"after",
		),
	};
}

export type EventData = {
	_id: string;
	AdditionalNotes: string;
	MasterEventId?: number;
	Location: {
		Lat: number;
		Lon: number;
		Address: string;
	};
	Approval: {
		Required: boolean;
		Compulsory: boolean;
		FormUrl: string;
		MoreInfoUrl: string;
		MedicalCheck: boolean;
		EmergencyContact: boolean;
		PermissionLockDays: number;
		FirstReminderDays: number;
		SecondReminderDays: number;
		TermsAndConditions: string;
	};
	Banner: {
		_id: string;
		ImageUrl: string;
	};
	CarerApprovals: [id: string, value: string];
	Forms: [id: string, value: string];
	SelectedGroups: [string];
	Notices: [string];
};

/**
 * Body sent when creating an event.
 *
 * Deliberately the minimum a new event needs to exist and show on the grid;
 * everything else (groups, notices, permissions, banner) is filled in on the
 * editor page afterwards. `_id` is the server's to assign.
 */
export type NewEventPayload = {
	Title: string;
	StartDateTime: string;
	EndDateTime: string;
	Category: string;
	/**
	 * Optional, and the only editor field accepted at creation.
	 *
	 * It is here because it chooses the sign-off workflow. Left to the follow-up
	 * PUT, an event could be submitted for approval before it had a type at all
	 * and resolve down its calendar's chain, reaching people who should never
	 * have seen it. Omitted or blank simply means no type, which resolves on the
	 * calendar as before.
	 */
	EventType?: string;
	/** The pre-approval form's answers, stored on the event as sent. */
	PreApproval?: Record<string, string | boolean>;
	/**
	 * Repeat the event. The start and end above describe the FIRST occurrence;
	 * the API creates the rest under one `MasterEventId` and answers with the
	 * first, plus `Series.EventIds`. Absent for a one-off event.
	 */
	Recurrence?: RecurrenceRule;
};

interface EventListResponse {
	permissions: Record<string, boolean>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	events: any[];
	/**
	 * How many events matched BEFORE `limit`, or null when `with_total=true` was
	 * not sent. Null and 0 are different answers and the API is careful to keep
	 * them apart, so this is `number | null` rather than an optional number.
	 */
	total?: number | null;
}

interface EventDetailResponse {
	permissions: Record<string, boolean>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	event: any;
}

/** List query cache shape — permissions must live here so cache hits still hydrate `canEdit`.

    `permissions` is OPTIONAL because a result can be produced without asking the
    API — see `emptyList`. Undefined means "nobody has told us", which is not the
    same as an empty map meaning "no flags", and the two must not be conflated. */
interface EventListQueryData {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	events: any[];
	permissions?: Record<string, boolean>;
}

/** Detail query cache shape — same pattern for single-event cache hits. */
interface EventDetailQueryData {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	event: any;
	permissions: Record<string, boolean>;
}

// Parse API response, handling string/NaN edge cases
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseResponse = (res: any): any => {
	if (typeof res === "string") {
		const sanitizedJson = res.replace(/:\s*NaN/g, ": null");
		return JSON.parse(sanitizedJson);
	}
	return res;
};

// A factory, not a shared constant: the caller puts `events` straight into
// state and the calendar mutates that array when an event is dragged, so a
// shared instance would leak one page's edits into the next empty result.
//
// PERMISSIONS ARE UNDEFINED HERE, NOT `{}`. This result can come from the
// short-circuit below, which answers without calling the API at all — so it
// knows nothing about the caller's flags. Returning an empty map instead said
// the caller HAS no flags, and `canEdit` believed it: the sign-off screen,
// which asks for no categories because it only ever wanted the flags, told an
// event administrator they did not have permission to sign off events.
const emptyList = () => ({
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	events: [] as any[],
	permissions: undefined as Record<string, boolean> | undefined,
});

/**
 * Fetch one page of the event list.
 *
 * Shared by the initial query, `getLazyData` (which built the same request twice
 * and would otherwise have to be kept in step by hand) and `useBrowseEvents`,
 * which pages over it a month at a time.
 *
 * With nothing ticked it returns an empty list WITHOUT calling the API: an empty
 * `categories` would be read as "no filter" and hand back every source, and
 * unticking every box must not fill the calendar.
 *
 * `supervisors` asks the API to name each event's supervising staff. Off by
 * default — see `listQueryString`. A caller that turns it on must not share a
 * query key with one that leaves it off, or the two will read each other's
 * payload; today the three callers sit in separate key namespaces
 * (`events/list`, `events/browse`, `event/concurrent`) and only the first asks.
 */
export async function fetchEventList(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	instance: any,
	from: Date,
	to: Date,
	categories: string[],
	tags: string[] = [],
	options: { supervisors?: boolean } = {},
) {
	if (categories.length === 0) return emptyList();
	const endpoint =
		EVENT_END_POINT +
		listQueryString(from, to, categories, tags, options.supervisors);
	const res = await callPymbleApi(instance, "GET", endpoint);
	if (!res) return emptyList();
	const parsed: EventListResponse = parseResponse(res);
	return {
		events: parsed.events.map(toCalendarEvent),
		permissions: parsed.permissions ?? {},
	};
}

/**
 * Rows per search request.
 *
 * A search carries no date window, so its result set is bounded only by how
 * common the word is: "test" matches a few hundred events across the whole
 * collection, "year" several thousand. 500 is enough that most searches arrive
 * complete in one request, and small enough that a two-letter term does not
 * pull ten thousand documents through three enrichment round-trips before
 * anyone has decided whether it was the search they meant.
 *
 * The rest is a page away. `total` says how many are being held back.
 */
export const SEARCH_PAGE_SIZE = 500;

/**
 * Build the query string for a search.
 *
 * NO DATE BOUNDS, which is the whole point: the browse views hold four months
 * at a time, so a client-side search over what they had fetched could only ever
 * find events in those four months while reporting counts that read as totals.
 * Both bounds are optional on the endpoint, so omitting them searches the
 * collection.
 *
 * `page` is 1-indexed, per the API — `page=0` there means "no pagination", not
 * "the first page".
 *
 * `sort` IS NOT SENT, and must not be. It is declared `Literal[-1, 1]` on the
 * endpoint, and a query string carries text: pydantic will not coerce "1" into
 * the int literal 1, so any value at all comes back 422. Ascending is the
 * default there, which is what a calendar reads in and the order Load more has
 * to extend, so the parameter has nothing to offer this caller even once it is
 * fixed.
 */
function searchQueryString(
	search: string,
	categories: string[],
	page: number,
	tags: string[] = [],
): string {
	return (
		"?limit=" +
		SEARCH_PAGE_SIZE +
		"&page=" +
		page +
		/* Without this the response cannot tell 500 results from 500 of 4,000,
		   which is the number the list has to show before anyone can trust it. */
		"&with_total=true&search=" +
		encodeURIComponent(search) +
		"&categories=" +
		encodeURIComponent(categories.join(",")) +
		"&include_cancelled=false" +
		/* The rail's tag ticks narrow a search as well as a date range. Left off,
		   ticking a tag would visibly do nothing the moment somebody typed in the
		   search box — the one place the two filters are most likely to be used
		   together. Omitted when empty, as in `listQueryString`. */
		(tags.length ? "&tags=" + encodeURIComponent(tags.join(",")) : "")
	);
}

export interface EventSearchPage {
	/** 1-indexed, matching the request. */
	page: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	events: any[];
	permissions: Record<string, boolean> | undefined;
	/** Matches before `limit`, or null if this API predates `with_total`. */
	total: number | null;
}

/**
 * Fetch one page of search results.
 *
 * The term goes to the API rather than being applied here. That is not just a
 * question of where the work happens: the endpoint matches `Title` OR
 * `Description.Short`, where the client-side pass in `eventFacets` only ever
 * looked at the title. Re-applying that pass over these results would drop
 * every description match on the floor after paying to fetch it, so the caller
 * must NOT filter on `searchText` again — see `buildFacets`'s `searchApplied`.
 */
export async function fetchEventSearch(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	instance: any,
	search: string,
	categories: string[],
	page: number,
	tags: string[] = [],
): Promise<EventSearchPage> {
	/* Same short-circuit as `fetchEventList`: an empty `categories` reads as "no
	   filter" at the API and would hand back every source. */
	if (categories.length === 0 || !search) {
		return { page, ...emptyList(), total: 0 };
	}
	const endpoint =
		EVENT_END_POINT + searchQueryString(search, categories, page, tags);
	const res = await callPymbleApi(instance, "GET", endpoint);
	if (!res) return { page, ...emptyList(), total: 0 };
	const parsed: EventListResponse = parseResponse(res);
	return {
		page,
		events: parsed.events.map(toCalendarEvent),
		permissions: parsed.permissions ?? {},
		/* `?? null` covers an API that does not know the parameter yet: the caller
		   reads null as "unknown", not as "none", and falls back to guessing from
		   whether the page came back full. */
		total: parsed.total ?? null,
	};
}

/** The ids out of a list that may hold ids, documents, or both. */
const idsOf = (list: unknown[]): string[] =>
	list
		.map((entry) =>
			typeof entry === "string"
				? entry
				: ((entry as { _id?: unknown } | null)?._id ?? null),
		)
		.filter((id): id is string => typeof id === "string");

/**
 * The draft, in the shape a PUT accepts.
 *
 * `Notices` AND `SelectedGroups` MEAN DIFFERENT THINGS IN EACH DIRECTION, which
 * is the whole reason this exists. The stored fields are `Notice` and
 * `SelectedGroups`, both lists of id strings. On READ the API resolves them into
 * `Notices` and `Groups`, arrays of small documents carrying the title, date,
 * summary and image a card needs. On WRITE the update model declares
 * `Notices: list[str]` and `SelectedGroups: list[str]` and writes them straight
 * back to the stored ids.
 *
 * So the editor loads documents into a draft and PUTs the draft back, and every
 * one of them comes back 422 `Input should be a valid string`. It is not only
 * about linking a new notice, though that is where it shows up first and worst
 * (the Notices tab keeps the whole search result, so index 0 is a notice
 * document complete with Groups, Individuals and Notifications). An event that
 * merely HAS a linked notice fails the same way on any save at all, because the
 * enriched read shape is objects too.
 *
 * Normalising here rather than in the tabs is deliberate. The Notices tab needs
 * the document: a notice linked a moment ago has no entry in the event yet, so
 * without it the card has no banner, no author and no summary to draw. The
 * draft is allowed to be rich; the request is not.
 *
 * A key absent from the draft is LEFT ABSENT rather than sent as `[]`. Both
 * fields default to an empty list on the model and are written unconditionally,
 * so an empty array is a real instruction to clear the stored list, and this
 * function is not the place to decide that.
 */
export function toWriteShape(eventData: EventData): EventData {
	const doc = eventData as unknown as Record<string, unknown>;
	const next: Record<string, unknown> = { ...doc };
	// Derived by the API on read, never an input.
	delete next.Series;
	// The API reads Approval flat; see utils/approvalWrite.ts.
	if (doc.Approval !== undefined) next.Approval = withFlatApproval(doc.Approval);
	// The read adds Label/Description; the write model refuses them.
	if (doc.Transport) next.Transport = pickTransport(doc.Transport);
	for (const key of ["Notices", "SelectedGroups"]) {
		const list = doc[key];
		if (Array.isArray(list)) next[key] = idsOf(list);
	}
	return next as unknown as EventData;
}

/**
 * The cache key every reader of one event shares.
 *
 * Exported because the calendar's hover card now reads the same document the
 * editor does — see `useEventOwner`. Two spellings of this key would mean two
 * fetches of a roll that is expensive to build, and the editor would not be
 * warm when the hover card had already paid for it.
 */
export const eventDetailKey = (eventId: string) => ["event", "v2", eventId];

/**
 * Fetch one event, roll and all.
 *
 * `attendees=1` is what makes this expensive and what makes it worth doing: the
 * API builds `Attendees` at READ time from the EventRelation edges, and only
 * for a single-event fetch. The list endpoint never enriches, so an event off
 * the calendar carries `Attendees: []` no matter how many supervisors it has.
 * Anything that needs to know who is running an event has to come through here.
 *
 * Shared by the editor's query and by the hover card's owner lookup, so both
 * write the one cache entry in the one shape.
 */
export async function fetchEventDetail(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	instance: any,
	eventId: string,
): Promise<EventDetailQueryData | null> {
	const endpoint = EVENT_END_POINT + eventId + "?attendees=1";
	const res = await callPymbleApi(instance, "GET", endpoint);
	if (!res) return null;
	const parsed: EventDetailResponse = parseResponse(res);
	return {
		event: parsed.event,
		permissions: parsed.permissions ?? {},
	};
}

/**
 * Hook to fetch api data from event admin S2 endpoints.
 *
 * `categories` are the event sources to fetch. They are part of the query key,
 * so ticking a box in the filter rail refetches that date range rather than
 * filtering what is already in hand — the whole point is that the excluded
 * events never cross the wire.
 *
 * `tags` is the second server-side filter and works the same way, with one
 * difference that matters here: an empty `categories` short-circuits the request
 * entirely, while an empty `tags` simply asks for no tag filter.
 */
export function useEventManager(
	eventId?: string,
	categories: string[] = [],
	tags: string[] = [],
) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const queryClient = useQueryClient();

	// Mutable events state — the Event page mutates this directly for form editing
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [events, setEvents] = useState<any>([]);
	const [permissions, setPermissions] = useState<Record<string, boolean>>({});

	// Date range for list mode — changed by getLazyData
	const initialDateRange = useMemo(() => {
		const today = new Date();
		return {
			from: new Date(today.getFullYear(), today.getMonth(), 1),
			to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
		};
	}, []);

	const [dateRange, setDateRange] = useState(initialDateRange);

	// Sorted so that ticking A then B and ticking B then A share one cache entry.
	const categoryKey = useMemo(
		() => [...categories].sort().join(","),
		[categories],
	);

	/* Same trick for tags, lower-cased as well: the API matches them
	   case-insensitively, so ticking "Sport" and ticking "sport" are one request
	   and must not be two cache entries. */
	const tagKey = useMemo(
		() =>
			[...tags]
				.map((t) => t.toLocaleLowerCase("en-AU"))
				.sort()
				.join(","),
		[tags],
	);

	// Query: event list (Home page, no eventId)
	const listQuery = useQuery({
		queryKey: [
			"events",
			"list",
			"v2",
			dateRange.from.toISOString(),
			dateRange.to.toISOString(),
			categoryKey,
			tagKey,
		],
		queryFn: () =>
			fetchEventList(instance, dateRange.from, dateRange.to, categories, tags, {
				supervisors: true,
			}),
		enabled: isAuthenticated && !eventId,
	});

	// Query: single event (Event page, with eventId)
	const eventQuery = useQuery({
		queryKey: eventDetailKey(eventId as string),
		queryFn: () => fetchEventDetail(instance, eventId as string),
		enabled: isAuthenticated && !!eventId,
	});

	// Seed events state from query results
	useEffect(() => {
		const d = listQuery.data as unknown;
		if (d == null) return;
		const payload = d as EventListQueryData;
		setEvents(payload.events);
		/* Only when the answer came from the API. A result the short-circuit
		   made up would otherwise overwrite what we already knew with nothing. */
		if (payload.permissions) setPermissions(payload.permissions);
	}, [listQuery.data]);

	useEffect(() => {
		const d = eventQuery.data;
		if (d == null) return;
		const payload = d as EventDetailQueryData;
		setEvents(payload.event);
		setPermissions(payload.permissions);
	}, [eventQuery.data]);

	// getLazyData — called by Mobiscroll onPageLoading with new date range.
	// Uses queryClient.fetchQuery so it returns a promise (preserving the
	// existing interface that Home.tsx relies on).
	const getLazyData = useCallback(
		async (from_date: Date, to_date: Date) => {
			const data = await queryClient.fetchQuery({
				queryKey: [
					"events",
					"list",
					"v2",
					from_date.toISOString(),
					to_date.toISOString(),
					categoryKey,
					tagKey,
				],
				queryFn: () =>
					fetchEventList(instance, from_date, to_date, categories, tags, {
						supervisors: true,
					}),
			});
			setEvents(data.events);
			if (data.permissions) setPermissions(data.permissions);
			setDateRange({ from: from_date, to: to_date });
		},
		/* `categories` and `categoryKey` belong here as much as the two the
		   merge left: the fetch is now filtered by source, so a callback holding
		   the tick list from first render would go on asking for the sources the
		   person had then. */
		[instance, queryClient, categories, categoryKey, tags, tagKey],
	);

	// Mutation: update event
	const updateMutation = useMutation({
		mutationFn: async (eventData: EventData) => {
			const endpoint = EVENT_END_POINT + eventData["_id"];
			return callPymbleApi(instance, "PUT", endpoint, {
				data: toWriteShape(eventData),
			});
		},
		/* Drop the cached reads, exactly as `moveEvent` does and for the same
		   reason: `staleTime` is an hour, so nothing refetches on mount. Saved
		   edits went to the database and the editor went on serving the document
		   it had loaded before the save — reopening the event showed the old
		   values, which reads as the save having failed. Requirements made it
		   obvious because a tick that reappears unticked is unmistakable.

		   The publish keys go too. The server decides things this write cannot:
		   it stamps `AddedAt`/`AddedBy` on a new requirement, and a material
		   edit invalidates a sign-off in progress, dropping the workflow back to
		   `changes_requested`. Both only reach the screen on a refetch.

		   `["events"]`, NOT `["events", "list"]`. There are THREE caches of
		   events and that prefix reaches only one of them:

		     ["events", "list",   ...]  the calendar's date range
		     ["events", "browse", ...]  the Cards and List views, paged by month
		     ["events", "search", ...]  the search results

		   react-query matches by key prefix, so naming "list" refreshed the
		   calendar and left the other two serving whatever they had loaded — for
		   an hour, since that is the staleTime and refetchOnWindowFocus is off.
		   Editing an event and going back to Cards showed the old title, the old
		   chips and the old tags, which reads as the save having failed. The bare
		   prefix covers all three, and every one of them wants refreshing for the
		   same reason. */
		onSuccess: (_result, eventData) => {
			const id = eventData?._id;
			if (id) {
				queryClient.invalidateQueries({ queryKey: ["event", "v2", id] });
				queryClient.invalidateQueries({
					queryKey: ["publish", "workflow", id],
				});
			}
			queryClient.invalidateQueries({ queryKey: ["events"] });
			queryClient.invalidateQueries({ queryKey: ["publish", "list"] });
			/* The vocabulary can have grown: the API adds a tag nobody has used
			   to the shared list as the event saves. Without this the tag the
			   person just coined is missing from every other event's picker and
			   from the filter rail until the hour is up. */
			queryClient.invalidateQueries({ queryKey: ["tags"] });
		},
	});

	const updateEvents = async (eventData: EventData) => {
		try {
			return await updateMutation.mutateAsync(eventData);
		} catch (error) {
			console.error(`ERROR: Could not update event.  ${error}`);
			throw error;
		}
	};

	/**
	 * Mutation: create event.
	 *
	 * `POST event/admin/` is LIVE, and this comment used to say at length that
	 * it was not. It landed after that was written and the note was never
	 * revised, so it went on telling every reader the create flow could not
	 * possibly work — which cost time twice while the flow was being built,
	 * because the obvious next move on reading it is to go and check the API
	 * rather than the code in front of you.
	 *
	 * What it does: takes `EventCreateRequest` (Title, StartDateTime,
	 * EndDateTime, Category, optional EventType), requires `event_admin`, and
	 * returns the created document in the same shape as `GET /{id}`. Times are
	 * coerced to local-naive Sydney server-side, so a browser `toISOString()`
	 * is converted back to school time rather than taken literally. The new
	 * `_id` is derived from title + start + creator + the instant, so a
	 * double-submit inside the same moment answers 409 rather than making two
	 * events.
	 */
	const createMutation = useMutation({
		mutationFn: async (eventData: NewEventPayload) =>
			callPymbleApi(instance, "POST", EVENT_END_POINT, { data: eventData }),
		onSuccess: () => {
			/* Drop every cached range so the new event appears — on the calendar,
			   and in the Cards, List and search views, which are separate caches
			   under this same prefix. See the note on the update mutation. */
			queryClient.invalidateQueries({ queryKey: ["events"] });
		},
	});

	const createEvent = async (eventData: NewEventPayload) => {
		try {
			return await createMutation.mutateAsync(eventData);
		} catch (error) {
			// No payload echoed — API errors can quote the request back, and the
			// event document carries attendee PII.
			console.error("ERROR: Could not create event.");
			throw error;
		}
	};

	/**
	 * Move an event to a new start/end, from a calendar drag.
	 *
	 * Sends the WHOLE document with only the dates swapped, not a dates-only
	 * patch. The API's update builder writes `Approval.*`, `Banner`,
	 * `SelectedGroups`, `Notice` and `ActiveAt` unconditionally, so a minimal
	 * payload does not leave them alone — it blanks them, and clearing
	 * `ActiveAt` would quietly withdraw the permission from carers. The list
	 * endpoint applies no projection, so the calendar already holds the full
	 * document and no extra read is needed.
	 *
	 * `StartDateTime`/`EndDateTime` are the exception: the API only writes those
	 * when they are sent, and rejects `end <= start` with a 400 of its own.
	 *
	 * Sending the whole document is exactly why it needs `toWriteShape` as much
	 * as the editor's save does. The list endpoint applies no projection, so the
	 * calendar holds the ENRICHED read shape — `Notices` as notice documents,
	 * not ids — and spreading that straight back into a PUT is a 422
	 * `Notices.0: Input should be a valid string` on any event that has a notice
	 * linked. Dragging an unlinked event worked, which is what made this look
	 * like a problem with the notice rather than with the move.
	 */
	const moveEvent = useCallback(
		async (event: Record<string, unknown>, start: string, end: string) => {
			const id = event["_id"];
			if (typeof id !== "string" || !id) {
				throw new Error("Event has no id");
			}
			const result = await callPymbleApi(
				instance,
				"PUT",
				EVENT_END_POINT + id,
				{
					data: toWriteShape({
						...event,
						StartDateTime: start,
						EndDateTime: end,
					} as unknown as EventData),
				},
			);

			/* Drop both cached reads, or the move is invisible everywhere except
			   the calendar that made it.

			   `staleTime` is an hour, so neither query refetches on mount: open
			   the event after dragging it and the editor would serve the cached
			   document with the OLD dates, and navigating away and back to the
			   calendar would put the event back in its old slot. The optimistic
			   update only touches local state, which does not survive a remount.

			   `["events"]` covers all three event caches — the calendar's ranges,
			   the Cards/List months and the search results. A drag changes the
			   event's dates, which decides which month a browse page holds it in,
			   so the browse cache is if anything the one most in need of it. */
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["event", "v2", id] }),
				queryClient.invalidateQueries({ queryKey: ["events"] }),
			]);

			return result;
		},
		[instance, queryClient],
	);

	// For single-event mode, derive from whether events state has been
	// seeded (object, not the initial []).  The useEffect that copies
	// eventQuery.data → events runs one render after isPending flips,
	// so checking isPending alone would let the form render with [].
	const eventsLoaded = eventId
		? events != null && typeof events === "object" && !Array.isArray(events)
		: !listQuery.isPending;

	/**
	 * Read the permission from the query data, not from the state the effects
	 * seed.
	 *
	 * Those effects run one render AFTER `isPending` flips (the same lag the
	 * comment above describes for `events`), so for exactly one render the data
	 * had arrived while `permissions` was still `{}` — and `canEdit` was false
	 * when it should not have been. That was invisible while the only thing it
	 * gated was a button that appeared a frame late; the approvals screen turns
	 * the same render into "you do not have permission", which then flickers
	 * away. Derived here, it is right in the render the data lands.
	 *
	 * The state stays as the fallback: `getLazyData` writes it for a range
	 * fetched through `fetchQuery`, which lands under a different cache key
	 * than the one `listQuery` is watching.
	 */
	const fetchedPermissions = eventId
		? (eventQuery.data as EventDetailQueryData | null)?.permissions
		: (listQuery.data as EventListQueryData | undefined)?.permissions;

	/* `??`, so an unfetched result falls back to the last thing the API said
	   rather than to nothing. */
	const knownPermissions = fetchedPermissions ?? permissions;
	const canEdit = knownPermissions?.event_admin === true;

	return {
		events,
		eventsLoaded,
		/* The whole flag map, not just canEdit. The sign-off screen needs the
		   elevated publishing flags to decide what it may offer, and deriving
		   another boolean here for each one would mean editing this hook every
		   time a flag is added. */
		permissions: knownPermissions,
		eventLoadError: eventQuery.error,
		eventSaving: updateMutation.isPending,
		canEdit,
		getLazyData,
		updateEvents,
		moveEvent,
		setEvents,
		createEvent,
		eventCreating: createMutation.isPending,
	};
}
